import { Injectable } from '@angular/core';
import { FormArray, FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { UserApplicationForm, UserForm, UserOrganisationForm } from '../models/forms/user-form';
import { Application } from '../models/application';
import { LaunchpadUser } from '../models/launchpad-user';
import { Organisation } from '../models/organisation';

@Injectable({
  providedIn: 'root'
})
export class UserFormService {

  private defaultApplications: { application: string; roles: string[] }[] = [
    { application: 'launchpad', roles: [ 'user' ] }
  ]

  constructor(private fb: NonNullableFormBuilder) { }

  createUserForm(): FormGroup<UserForm> {
    return this.fb.group<UserForm>({
      organisations: this.fb.control([], Validators.minLength(1)),
      emailAddress: this.fb.control('', [Validators.required, Validators.email, Validators.maxLength(100)]),
      firstName: this.fb.control('', [Validators.required, Validators.maxLength(50)]),
      surname: this.fb.control('', [Validators.required, Validators.maxLength(100)]),
      applications: this.fb.array([].map(this.createUserApplicationForm),
        [Validators.required, Validators.minLength(1)]),
      disableTeamMemberRestriction: this.fb.control(false),
    })
  }

  createUserOrganisationForm(): FormGroup<UserOrganisationForm> {
    return this.fb.group<UserOrganisationForm>({
      organisationRef: this.fb.control('', Validators.required),
      name: this.fb.control('')
    })
  }

  createUserApplicationForm(): FormGroup<UserApplicationForm> {
    return this.fb.group<UserApplicationForm>({
      applicationId: this.fb.control('', Validators.required),
      identifier: this.fb.control(''),
      roles: this.fb.control([], [Validators.required, Validators.minLength(1)])
    })
  }

  createPopulatedUserApplicationForm(applicationId: string, identifier: string, roles: string[]): FormGroup<UserApplicationForm> {
    return this.fb.group<UserApplicationForm>({
      applicationId: this.fb.control(applicationId, Validators.required),
      identifier: this.fb.control(identifier),
      roles: this.fb.control(roles ?? [], [Validators.required, Validators.minLength(1)])
    })
  }

  populateForm(userForm: FormGroup<UserForm>, user: LaunchpadUser): void {
    userForm.patchValue(user);
    userForm.controls.emailAddress.disable();

    if (!user?.organisations || user?.organisations.length == 0) {
      return;
    }

    userForm.controls.organisations.patchValue(
      user.organisations.map(o => {
        return {
          organisationRef: o.organisationRef,
          name: o.name
        };
    }));

    this.populateApplicationForm(userForm, user.organisations);
  }

  populateApplicationForm(userForm: FormGroup<UserForm>, userOrganisations: Organisation[]): void {
    const userAppList = userOrganisations.map(o => o.applications).flat();
    const appMap = new Map(userAppList.map(item => [item.id, item]));
    const userApps = [...appMap.values()];

    userApps
      .filter(a => a.roles && a.roles.length > 0)
      .forEach(application => {
        userForm.controls.applications.push(this.createPopulatedUserApplicationForm(application.id, application.identifier, application.roles.map(r => r.name)));
      });
  }

  /**
   * If the application role is in the default applications and available for the user to pick,
   * assign it to the user's application roles automatically
   */
  prepopulateApplicationsWithDefault(
    applicationsForm: FormArray<FormGroup<UserApplicationForm>>, availableApplications: Application[]): void {

    this.defaultApplications
      .forEach(defaultApp => {
        let application = availableApplications.find(a => a.name.toLowerCase() == defaultApp.application);

        if (!application)
          return;

        let roles = application.roles
          .filter(r => defaultApp.roles.findIndex(ar => ar == r.name.toLowerCase()) >= 0)
          .map(r => r.name);

        if (roles.length > 0) {
          applicationsForm.push(
            this.createPopulatedUserApplicationForm(application.id, application.identifier, roles));
        }

      });
  }

  refreshApplicationsForm(
    applicationsForm: FormArray<FormGroup<UserApplicationForm>>, availableApplications: Application[]): void {
    let selectedApps = applicationsForm.value;

    selectedApps = selectedApps
      .filter(a => a.applicationId && a.roles)
      .filter(a => availableApplications.map(app => app.id).includes(a.applicationId!))

    applicationsForm.clear();
    selectedApps.forEach(application => {
      applicationsForm.push(
        this.createPopulatedUserApplicationForm(application.applicationId!, application.identifier!, application.roles!));
    });
  }
}
